/* eslint-disable react-hooks/exhaustive-deps */
import {
  ClockCircleOutlined,
  CreditCardOutlined,
  EditOutlined,
  HomeOutlined,
  IdcardOutlined,
  LinkOutlined,
  MailOutlined,
  SyncOutlined,
  TagOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Breadcrumb,
  Button,
  Divider,
  Image,
  message,
  Modal,
} from 'antd';
import AddToSlackButton from 'components/addToSlackButton';
import AnimatedDot from 'components/animatedDot';
import EditUserModal from 'components/editUserModal';
import EditWorkspaceInfoModal from 'components/editWorkspaceInfoModal';
import Header from 'components/header';
import PageWrapper from 'components/pageWrapper';
import dayjs from 'dayjs';
import pluralize from 'pluralize';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { logout } from 'services/api/users';
import {
  removeSlackWorkspace,
  syncSlackWorkspace,
} from 'services/api/workspace';
import ProfileContext from 'services/profileContext';
import styles from 'styles/settings.module.scss';
import { getApiURL, getSessionId, isAdmin } from 'utils/utils';

const Settings = () => {
  const { profile, clearProfile, updateProfile } = useContext(ProfileContext);
  const navigate = useNavigate();
  const [isEditWorkspaceInfoModalOpen, setIsEditWorkspaceInfoModalOpen] =
    useState(false);
  const [isUpdateProfileModalOpen, setIsUpdateProfileModalOpen] =
    useState(false);
  const [removeSlackWorkspaceLoading, setRemoveSlackWorkspaceLoading] =
    useState(false);
  const [syncSlackWorkspaceLoading, setSyncSlackWorkspaceLoading] =
    useState(false);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('slackSuccess') === 'true') {
      message.success('Slack workspace connected successfully', 5);
    }
    if (params.get('slackError') === 'true') {
      message.success('Issue while connecting Slack workspace', 5);
    }
    const p = params.get('param');
    if (p && p.substring(0, 10) === 'onboarding') {
      navigate('/onboarding?slackSuccess=true');
    } else if (p && p.substring(0, 18) === 'selectParticipants') {
      const cycleId = p.substring(18);
      navigate(`/cycles/${cycleId}?tab=participants&slackSuccess=true`);
    } else if (p && p.substring(0, 11) === 'createcycle') {
      const cycleId = p.substring(11);
      navigate(`/create-cycle?cycleid=${cycleId}&slackSuccess=true`);
    }
  }, []);

  const resetPasswordFunction = () => {
    //logout and redirect to forget pwd
    logout(() => {
      navigate('/forgot-password');
      clearProfile();
    });
  };

  const handleRemoveSlackWorkspace = () => {
    setRemoveSlackWorkspaceLoading(true);
    removeSlackWorkspace()
      .then(() => {
        updateProfile();
        message.success('Slack workspace removed');
      })
      .catch((err) => {
        message.error(err.message);
        message.error('Failed to remove Slack workspace');
      })
      .finally(() => {
        setRemoveSlackWorkspaceLoading(false);
      });
  };

  const handleGetCustomerPortal = () => {
    window.location.href = `${getApiURL()}/web-app/admin/workspaces/customer-portal?sessionid=${getSessionId()}`;
  };
  const handleSyncSlackWorkspace = () => {
    setSyncSlackWorkspaceLoading(true);
    syncSlackWorkspace()
      .then(() => {
        message.success('Slack workspace synced');
      })
      .catch((err) => {
        console.error(err);
        if (err.response.data.error.message === 'RATE_LIMITED') {
          message.error(
            "You've reached the maximum number of syncs, wait 1h before trying again"
          );
        } else {
          message.error('Failed to sync Slack workspace');
        }
      })
      .finally(() => {
        setSyncSlackWorkspaceLoading(false);
      });
  };

  return (
    <>
      <Header />
      <div className={styles.wrapper}>
        <PageWrapper width={900}>
          <Breadcrumb
            items={[
              {
                title: (
                  <a href="/#/dashboard">
                    <HomeOutlined />
                    &nbsp;Dashboard
                  </a>
                ),
              },
              {
                title: 'Settings',
              },
            ]}
          />
          <h1>Settings</h1>
          <div className={styles.accountInfo}>
            <h2>Personal settings</h2>
            <div className={styles.accountInfoElement}>
              <Avatar
                size={40}
                src={profile.profile?.profilePictureUrl}
                icon={<UserOutlined />}
              />
            </div>
            <div className={styles.accountInfoElement}>
              <div className={styles.label}>
                <UserOutlined />
                &nbsp;Name
              </div>
              <div>
                {profile.profile?.firstName} {profile.profile?.lastName}
              </div>
            </div>
            <div className={styles.accountInfoElement}>
              <div className={styles.label}>
                <TagOutlined />
                &nbsp;Job title
              </div>
              <div>{profile.profile?.jobTitle || '-'}</div>
            </div>
            <div className={styles.accountInfoElement}>
              <div className={styles.label}>
                <MailOutlined />
                &nbsp;Email
              </div>
              <div>{profile.email}</div>
            </div>
            <br />
            <br />
            <div style={{ display: 'flex', gap: '12px' }}>
              <Button onClick={resetPasswordFunction} type={'primary'} ghost>
                Update password
              </Button>
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  setIsUpdateProfileModalOpen(true);
                }}
                type={'primary'}
              >
                Edit my profile
              </Button>
            </div>
          </div>
          {profile.workspace && (
            <>
              <Divider />
              <div>
                <h2>Workspace settings</h2>
                <div className={styles.accountInfoElement}>
                  <div className={styles.label}>
                    <ClockCircleOutlined />
                    &nbsp;Timezone
                  </div>
                  <div>{profile.workspace?.tz}</div>
                </div>
                <div className={styles.accountInfoElement}>
                  <div className={styles.label}>
                    <IdcardOutlined />
                    &nbsp;Name
                  </div>
                  <div>{profile.workspace?.name}</div>
                </div>
                {isAdmin(profile) && (
                  <div style={{ marginTop: '24px' }}>
                    <Button
                      onClick={() => {
                        setIsEditWorkspaceInfoModalOpen(true);
                      }}
                      type={'primary'}
                      icon={<EditOutlined />}
                    >
                      Edit workspace information
                    </Button>
                  </div>
                )}
                {isAdmin(profile) && (
                  <div className={styles.accountInfoElement}>
                    <div className={styles.label}>
                      <LinkOutlined />
                      &nbsp;Integrations
                    </div>
                    <div>
                      {profile.workspace.slack ? (
                        <div className={styles.slackIntegrationRunning}>
                          <div className={styles.slackIntegrationRunningTitle}>
                            <Image
                              src="/img/slack_80.png"
                              width={20}
                              preview={false}
                            />
                            &nbsp;&nbsp;{profile.workspace.slack.name}
                          </div>
                          <div className={styles.slackIntegrationRunningDot}>
                            <AnimatedDot />{' '}
                            <div className={styles.slackIntegrationRunningText}>
                              Running
                            </div>
                          </div>
                          <div
                            className={styles.slackIntegrationRunningLastSync}
                          >
                            Last sync:{' '}
                            {dayjs(profile.workspace.slack.lastSyncedAt).format(
                              'DD/MM/YYYY HH:mm'
                            )}
                            <Button
                              type={'link'}
                              onClick={handleSyncSlackWorkspace}
                              loading={syncSlackWorkspaceLoading}
                              icon={<SyncOutlined />}
                            >
                              Sync now
                            </Button>
                          </div>
                          {isAdmin(profile) && (
                            <div>
                              <Button
                                type={'link'}
                                style={{ paddingLeft: 0, marginTop: 12 }}
                                danger
                                onClick={() => {
                                  Modal.confirm({
                                    title: 'Remove Slack workspace',
                                    content:
                                      'Are you sure you want to remove the Slack workspace?',
                                    onOk: () => {
                                      handleRemoveSlackWorkspace();
                                    },
                                    okButtonProps: {
                                      loading: removeSlackWorkspaceLoading,
                                    },
                                    okText: 'Yes, remove',
                                    cancelText: 'No, keep it',
                                  });
                                }}
                              >
                                Remove Slack workspace
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <AddToSlackButton />
                      )}
                    </div>
                  </div>
                )}

                {isAdmin(profile) && profile.workspace?.buddiesSubscription && (
                  <div className={styles.accountInfoElement}>
                    <div className={styles.label}>
                      <CreditCardOutlined />
                      &nbsp;Billing
                    </div>
                    {!profile.workspace?.slack && (
                      <div style={{ marginBottom: 12 }}>
                        <b>{profile.workspace?.seats}</b>{' '}
                        {pluralize('seat', profile.workspace?.seats)} used on your
                        workspace.
                      </div>
                    )}
                    <div>
                      <Button
                        type={'primary'}
                        onClick={handleGetCustomerPortal}
                      >
                        Manage subscription
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <br />
          <br />
          {isAdmin(profile) && (
            <EditWorkspaceInfoModal
              isOpen={isEditWorkspaceInfoModalOpen}
              onClose={() => setIsEditWorkspaceInfoModalOpen(false)}
            />
          )}
          {isUpdateProfileModalOpen && (
            <EditUserModal
              user={profile}
              open={isUpdateProfileModalOpen}
              onCancel={(refresh = false) => {
                if (refresh) {
                  updateProfile();
                }
                setIsUpdateProfileModalOpen(false);
              }}
            />
          )}
        </PageWrapper>
      </div>
    </>
  );
};

export default Settings;
